import { loadRemote } from '@module-federation/enhanced/runtime';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useAuth } from '~/auth/use-auth';

import type { NameRemotes } from '~/remotes';

interface UseRemoteAppOptions {
  parentElementId: string;
  remoteName: NameRemotes;
  eventListenerToRoutes?: boolean;
  componentName: string;
}

interface RemoteModule {
  inject: (parentElementId: string) => void;
  unmount: () => void;
}

export function useRemoteApp({
  parentElementId,
  remoteName,
  eventListenerToRoutes,
  componentName,
}: UseRemoteAppOptions): { loading: boolean } {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const { verifySession } = useAuth();

  // biome-ignore lint/correctness/useExhaustiveDependencies: effect should only run once on mount for remote app loading
  useEffect(() => {
    let unmountRemote: () => void;
    let isMounted = true;

    const handleNavigation = async (event: Event) => {
      const customEvent = event as CustomEvent<{ path: string }>;
      if (customEvent.detail?.path) {
        await verifySession();
        await navigate({ to: customEvent.detail.path });
      }
    };

    const loadRemoteApp = async () => {
      try {
        const module = await loadRemote<RemoteModule>(
          `${remoteName}/${componentName}`,
        );
        if (!module) return;
        const { inject, unmount } = module;
        unmountRemote = unmount;

        if (eventListenerToRoutes) {
          window.addEventListener(`${remoteName}_navigation`, handleNavigation);
        }

        inject(parentElementId);
      } catch (error) {
        console.error('Error loading the Remote:', error);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadRemoteApp();

    return () => {
      isMounted = false;
      if (unmountRemote) unmountRemote();
      if (eventListenerToRoutes) {
        window.removeEventListener(
          `${remoteName}_navigation`,
          handleNavigation,
        );
      }
    };
  }, []);

  return { loading };
}
